<template>
  <div :class="'assessment' + ' ' + 'assessment-' + assessment_id" data-testid="assessment">
    <v-dialog v-model="load_progress_dialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t("assessment_exercise.load_progress_dialog_title") }}</v-card-title>
        <v-card-text>
          {{ $t("assessment_exercise.load_progress_dialog_message") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectLoadProgress">{{ $t("assessment_exercise.start_fresh") }}</v-btn>
          <v-btn color="primary" @click="acceptLoadProgress">{{ $t("assessment_exercise.load_progress") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="current_clinic && current_user && loaded_assessment_grid && regimen">
      <sub-regimen
        :show_title="show_title"
        :title="loaded_assessment_grid.title"
        :regimen_override="regimen"
        :exercises="exercises"
        :video_exercises="video_exercises"
        :user="current_user"
        :clinic="current_clinic"
        :invite_id="invite_id"
        :report_tags="[
          'assessment',
          'assessment_exercise',
          'assessment_id(&quot;' + assessment_id + '&quot;)',
          'assessment_instance_id(&quot;' + assessment_instance_id + '&quot;)',
        ]"
        :progress="saved_progress"
        ephemeral
        @exercise_launched="exercise_launched"
        @exercise_finished="exercise_finished"
        @regimen_complete="regimen_complete"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
//import Regimen from "./Regimen.vue";
import axios from "axios";
import uuid from "uuid";

// TODO: Build out an assessment configurator.

export default {
  name: "Assessment",
  components: {
    // We import it asynchronously so that we can use Regimen recursively
    "sub-regimen": () => import("./Regimen.vue"),
  },
  props: {
    invite_id: {
      type: String,
      required: true,
    },
    assessment_id: {
      type: String,
      required: true,
    },
    show_title: {
      type: Boolean,
      default: () => true,
      required: false,
    },
    assessment_grid: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    const date = new Date();
    return {
      loaded_assessment_grid: this.assessment_grid,
      invite: null,
      assessment_instance_id: uuid.v4(),
      backend_reports: [],
      exercise_result: [],
      start_time: date.getTime(),
      events: {
        assessment_start: [
          {
            timestamp: date.getTime(),
            event_type: "assessment_start",
            assessment_id: this.assessment_id,
            assessment_instance_id: this.assessment_instance_id,
          },
        ],
        exercise_launched: [],
        exercise_finished: [],
        assessment_complete: [],
      },
      load_progress_dialog: false,
      saved_progress: null,
      saved_progress_data: null,
      is_loading_progress: false,
    };
  },
  computed: {
    ...mapState([
      "app_config",
      "current_user",
      "current_clinic",
      "user_regimens",
      "current_clinic_id",
      "exercises",
      "video_exercises",
      "current_progress_reports",
    ]),
    user_regimen() {
      for (let user_regimen of this.user_regimens) {
        if (user_regimen.clinic_id == this.current_clinic_id) {
          return user_regimen;
        }
      }
      return null;
    },
    assessment() {
      if (!this.app_config.assessments) return null;
      return this.app_config.assessments[this.assessment_id];
    },
    regimen() {
      if (!this.loaded_assessment_grid) {
        return null;
      }

      let regimen = {
        id: uuid.NIL,
        invite_id: this.invite_id,
        clinic_id: this.clinic_id,
        instructions: this.assessment.instructions,
        exercises: this.loaded_assessment_grid.exercises,
      };

      return regimen;
    },
  },
  mounted() {
    if (!this.assessment_grid) {
      this.loadAssessmentGrid().then(() => {
        this.checkForSavedProgress();
      });
    } else {
      this.checkForSavedProgress();
    }
  },
  methods: {
    getStorageKey() {
      return `assessment_progress_${this.assessment_id}_${this.invite_id}`;
    },
    checkForSavedProgress() {
      const storageKey = this.getStorageKey();
      const savedData = sessionStorage.getItem(storageKey);
      
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // Only show dialog if there's actual progress (backend_reports with items)
          if (parsed.backend_reports && parsed.backend_reports.length > 0) {
            this.load_progress_dialog = true;
            this.saved_progress_data = parsed;
          } else {
            // No real progress, just clear it
            sessionStorage.removeItem(storageKey);
          }
        } catch (e) {
          // Invalid data, remove it
          sessionStorage.removeItem(storageKey);
        }
      }
    },
    acceptLoadProgress() {
      this.is_loading_progress = true;
      const saved = this.saved_progress_data;
      
      // Restore assessment_instance_id
      this.assessment_instance_id = saved.assessment_instance_id;
      
      // Restore start_time
      this.start_time = saved.start_time;
      
      // Restore events
      this.events = saved.events;
      
      // Restore backend_reports both locally and for passing to Regimen
      this.backend_reports = saved.backend_reports || [];
      this.saved_progress = saved.backend_reports || [];
      
      // Update assessment_start event with restored instance_id
      if (this.events.assessment_start && this.events.assessment_start.length > 0) {
        this.events.assessment_start[0].assessment_instance_id = this.assessment_instance_id;
      }
      
      this.load_progress_dialog = false;
      this.is_loading_progress = false;
    },
    rejectLoadProgress() {
      // Clear the saved progress
      const storageKey = this.getStorageKey();
      sessionStorage.removeItem(storageKey);
      this.load_progress_dialog = false;
      this.saved_progress_data = null;
    },
    saveProgress() {
      if (this.is_loading_progress) {
        return; // Don't save while loading
      }
      
      const storageKey = this.getStorageKey();
      const dataToSave = {
        assessment_instance_id: this.assessment_instance_id,
        backend_reports: this.backend_reports,
        events: this.events,
        start_time: this.start_time,
      };
      
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(dataToSave));
      } catch (e) {
        // Storage might be full or unavailable, silently fail
        console.warn("Failed to save assessment progress to sessionStorage", e);
      }
    },
    clearProgress() {
      const storageKey = this.getStorageKey();
      sessionStorage.removeItem(storageKey);
    },
    exercise_launched(event_data) {
      const date = new Date();
      let event = {
        timestamp: date.getTime(),
        event_type: "exercise_launched",
        exercise_index: event_data.index,
        exercise_type: event_data.instance.exercise_type,
        exercise_id: event_data.instance.exercise_id,
        uuid: event_data.instance.uuid,
      };

      this.events["exercise_launched"].push(event);
      this.saveProgress();
    },
    exercise_finished(event_data) {
      const date = new Date();
      let event = {
        timestamp: date.getTime(),
        event_type: "exercise_finished",
        exercise_index: event_data.index,
        exercise_type: event_data.instance.exercise_type,
        exercise_id: event_data.instance.exercise_id,
        uuid: event_data.instance.uuid,
        report_id: event_data.backend_report.id,
        score: event_data.backend_report.score,
      };

      this.events["exercise_finished"].push(event);
      
      // Track backend_reports incrementally - add the new report
      // We need to find if there's already a report for this index and replace it, or add it
      if (event_data.backend_report) {
        // Find the report for this assigned exercise or create a new entry.
        const existingIndex = this.backend_reports.findIndex(
          (report) =>
            report.regimen_instance_id
            && report.regimen_instance_id === event_data.backend_report.regimen_instance_id
        );
        if (existingIndex >= 0) {
          this.backend_reports[existingIndex] = event_data.backend_report;
        } else {
          this.backend_reports.push(event_data.backend_report);
        }
      }
      
      this.saveProgress();
    },
    regimen_complete(results) {
      const date = new Date();
      this.events["assessment_complete"].push({
        timestamp: date.getTime(),
        event_type: "assessment_complete",
        assessment_id: this.assessment_id,
        assessment_instance_id: this.assessment_instance_id,
      });

      // Update backend_reports from results before saving
      this.backend_reports = results.reports || [];

      // Clear progress from sessionStorage when assessment is complete
      this.clearProgress();

      this.$emit("exercise-finished", {
        start_time: this.start_time,
        time_played: date.getTime() - this.start_time,
        results: results.results,
        reports: results.reports,
        invite_id: this.invite_id,
        assessment_id: this.assessment_id,
        assessment_instance_id: this.assessment_instance_id,
        assessment_grid: this.loaded_assessment_grid,
        events: this.events,
      });
    },
    loadAssessmentGrid() {
      return axios.get("/api/grid_set/" + this.assessment_id).then((resp) => {
        if (resp.data.status == "success") {
          this.loaded_assessment_grid = resp.data.data;
        } else {
          messageBus.$emit("api-error", resp.data);
        }
      });
    },
  },
};
</script>
